import { ref, type Ref } from "vue";

const apiHeaders = {
  "Accept": "application/json",
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest",
  // "X-Craft-Site": "...",
  // "X-CSRF-Token": "...",
};


export interface SessionInfo {
  isGuest: boolean
  timeout: number
  csrfTokenName: string
  csrfTokenValue: string
  id?: number
  uid?: string
  username?: string
  email?: string
  groups?: string[]
  admin?: boolean
}

export async function getSessionInfo() {
  return (await fetch("/actions/users/session-info", {
    headers: {
      ...apiHeaders,
      "Accept": "application/json",
    },
  })).json() as Promise<SessionInfo>;
};


export interface LoginResult {
  message: string,

  // on failure
  errorCode?: string,

  // on success
  returnUrl?: string,
  csrfTokenValue?: string,
  modelName?: string,
  modelClass?: string
  user?: {
		active: boolean,
		pending: boolean,
		locked: boolean,
		suspended: boolean,
		admin: boolean,
		username: string,
		email: string,
		lastLoginDate: string,  // iso
		hasDashboard: boolean,
		passwordResetRequired: boolean,
		lastPasswordChangeDate: null,
		unverifiedEmail: null | string,
		id: number,
		uid: string,
		dateCreated: string,  // iso
		dateUpdated: string,  // iso
		fullName: string,
		name: string,
		isCredentialed: boolean,
		isCurrent: boolean,
		preferredLanguage: string,
  }
}

export interface SendPasswordResetResult {
  message: string,
  // on failure
  errorCode?: string,
  // on success
  returnUrl?: string,
}

export interface ResetPasswordResult {
  message: string,
  // on failure
  errorCode?: string,
  // on success
  redirect?: string,
}

export interface FieldValues {
  [key: string]: string | string[] | FieldValues
}

export interface EntryOptions {
  // Encoded as canonicalId
  entryId?: number
  enabled?: boolean
  enabledForSite?: boolean | number[]
  siteId?: number
  sectionId?: number
  author?: number
  parentId?: number
  typeId?: number

  title?: string
  slug?: string | null
  postDate?: Date
  expiryDate?: Date | null
  provisional?: boolean
  revisionNotes?: string | null

  fields?: FieldValues,
}

export interface UserOptions {
  userId?: number
  // Required when updating `email` or setting a `newPassword`.
  currentPassword?: string

  username?: string
  fullName?: string
  email?: string
  newPassword?: string

  fields?: FieldValues,
}

export interface RequestResult {
  message: string,

  // on failure for saving entries or users
  errors?: Record<string, string[]>,

  // on failure for others
  errorCode?: string,

  // on success
  id?: number
  title?: string
  slug?: string
  authorUsername?: string
  dateCreated?: Date
  dateUpdated?: Date
  postDate?: Date
  modelClass?: string
  modelId?: number
  model?: object
}

export class Session {
  info: Ref<SessionInfo | undefined>;

  constructor() {
    if (window?.craft?.sessionInfo != null) {
      this.info = ref(window.craft.sessionInfo);
    } else {
      throw new Error("session unavailable")
    }
  }

  async login(username: string, password: string) {
    console.dir(this.info.value);
    return await (await fetch("/actions/users/login", {
      headers: apiHeaders,
      method: "post",
      body: JSON.stringify({
        [this.info.value!.csrfTokenName]: this.info.value!.csrfTokenValue,
        loginName: username,
        password: password,
        rememberMe: "y",
      }),
    })).json() as Promise<LoginResult>;
  }

  async sendPasswordResetEmail(username: string) {
    return await (await fetch("/actions/users/send-password-reset-email", {
      method: "post",
      headers: apiHeaders,
      body: JSON.stringify({
        [this.info.value!.csrfTokenName]: this.info.value!.csrfTokenValue,
        loginName: username
      }),
    })).json() as Promise<SendPasswordResetResult>;
  }

  async resetPassword(accountUid: string, code: string, newPassword: string) {
    return await (await fetch("/actions/users/set-password", {
      method: "post",
      headers: apiHeaders,
      body: JSON.stringify({
        [this.info.value!.csrfTokenName]: this.info.value!.csrfTokenValue,
        id: accountUid,
        code: code,
        newPassword: newPassword
      }),
    })).json() as Promise<ResetPasswordResult>;
  }

  // Graphql does not have granular permissions to restrict editing entries only
  // created by the entry author, so we are forced to use the controller action
  // instead :(
  async saveEntry(entry: EntryOptions) {
    interface StringRecord {
      [key: string]: string | string[] | StringRecord | StringRecord[] | null
    }

    const values: StringRecord = {
      [this.info.value!.csrfTokenName]: this.info.value!.csrfTokenValue,
    };

    if (entry.entryId !== undefined) { values.canonicalId = entry.entryId.toString(); }
    if (entry.enabled !== undefined) { values.enabled = entry.enabled ? "1" : "0"; }
    if (entry.enabledForSite !== undefined) {
      if (Array.isArray(entry.enabledForSite)) {
        values.enabledForSite = entry.enabledForSite.map(site => site.toString());
      } else {
        values.enabledForSite = entry.enabledForSite ? "1" : "0";
      }
    }
    if (entry.siteId !== undefined) { values.siteId = entry.siteId.toString(); }
    if (entry.sectionId !== undefined) { values.sectionId = entry.sectionId.toString(); }
    if (entry.author !== undefined) { values.author = entry.author.toString(); }
    if (entry.parentId !== undefined) { values.parentId = entry.parentId.toString(); }
    if (entry.typeId !== undefined) { values.typeId = entry.typeId.toString(); }

    if (entry.title !== undefined) { values.title = entry.title.toString(); }
    if (entry.slug !== undefined) {
      values.slug = (entry.slug == null) ? "" : entry.slug.toString();
    }
    if (entry.postDate !== undefined) { values.postDate = entry.postDate.toISOString(); }
    if (entry.expiryDate !== undefined) {
      values.expiryDate = (entry.expiryDate == null) ? "" : entry.expiryDate.toISOString();
    }
    if (entry.provisional !== undefined) { values.provisional = entry.provisional ? "1" : "0"; }
    if (entry.revisionNotes !== undefined) {
      values.revisionNotes = entry.revisionNotes == null ? "" : entry.revisionNotes;
    }

    function walk(node: object): StringRecord {
      const out: StringRecord = {};
      for (const [key, value] of Object.entries(node)) {
        if (typeof value === "object") {
          out[key] = walk(value);
        } else {
          out[key] = value.toString();
        }
      }
      return out;
    }

    if (entry.fields !== undefined) {
      values.fields = walk(entry.fields);
    }

    return await (await fetch("/actions/entries/save-entry", {
      method: "post",
      headers: apiHeaders,
      body: JSON.stringify(values),
    })).json() as Promise<RequestResult>;
  }

  async deleteEntry(entryId: number) {
    return await (await fetch("/actions/elements/delete", {
      method: "post",
      headers: apiHeaders,
      body: JSON.stringify({
        [this.info.value!.csrfTokenName]: this.info.value!.csrfTokenValue,
        elementId: entryId
      }),
    })).json() as Promise<RequestResult>;
  }

  async saveUser(user: UserOptions) {
    interface StringRecord {
      [key: string]: string | string[] | StringRecord | StringRecord[] | null
    }

    const values: StringRecord = {
      [this.info.value!.csrfTokenName]: this.info.value!.csrfTokenValue,
    };

    if (user.userId !== undefined) { values.userId = user.userId.toString(); }
    if (user.currentPassword !== undefined) { values.currentPassword = user.currentPassword; }

    if (user.username !== undefined) { values.username = user.username; }
    if (user.fullName !== undefined) { values.fullName = user.fullName; }
    if (user.email !== undefined) { values.email = user.email; }
    if ((user.email !== undefined || user.newPassword !== undefined) && (user.newPassword !== undefined)) {
      values.newPassword = user.newPassword;
    }

    function walk(node: object): StringRecord {
      const out: StringRecord = {};
      for (const [key, value] of Object.entries(node)) {
        if (typeof value === "object") {
          out[key] = walk(value);
        } else {
          out[key] = value.toString();
        }
      }
      return out;
    }

    if (user.fields !== undefined) {
      values.fields = walk(user.fields);
    }

    return await (await fetch("/actions/users/save-user", {
      method: "post",
      headers: apiHeaders,
      body: JSON.stringify(values),
    })).json() as Promise<RequestResult>;
  }
}
